import * as config from '../config';
import Application from '../Application';
import BaseView from './BaseView';
// @ts-ignore
import landmarks from '../data/jarlin_cabinets_landmarks.json';
import logging from '../logging';
import mfac from '../MaterialFactory';
import { Appliance, Cabinet, Room, Wall } from '../domain';
import { ExtrudeGeometry, Group, Mesh, Path, Shape, Vector2, Vector3 } from 'three';
import { Transform } from './CrownMoldingView';
import { cabinets, cabinetWidth } from '../data/JarlinCatalogData';
import { firstMatching, lookahead, ThreeAxisHelper } from '../utils';
import { uvDimensions, uvScale } from '../IIID';
import { trunc } from '../measurement';

const log = logging('view');

const APPROX_PI = trunc(Math.PI, 3);

const needsCountertop = (cabinet: Cabinet) => {
    return cabinets.boolean(cabinet.sku, 'countertop');
}

const cornerShouldBeFilled = (cabinet: Cabinet, wall: Wall, nextWall: Wall): boolean => {
    const 
        wallLength = wall.heights[wall.heights.length - 1].x,
        width = cabinetWidth(cabinet.sku);

    // If the next wall has a cabinet within 30 inches of this wall, then
    // we may want to extend the countertop around the corner. This assumes
    // the angle between walls is always negative. Note that since fillers
    // are a special kind of cabinet, and they will also receive countertop.
    let nextWallInRange = false;
    if(nextWall != null) {
        const startOfNextWall = firstMatching(nextWall.cabinets, needsCountertop);
        if(startOfNextWall != null) {
            // something with landmarks will add its own corner
            if(startOfNextWall.sku in landmarks) {
                return false;
            }

            const
                r1 = wall.rotation.y,
                r2 = nextWall.rotation.y,
                dr = trunc(Math.abs(r2 - r1), 3);
            if(dr == APPROX_PI) {
                return false;
            }

            nextWallInRange = startOfNextWall.position.x < 24;
        }
    }
    const inRange = cabinet.position.x + width + 24 >= wallLength;
    // if we do, and the first cabinet on the next wall is also
    // in range, then 
    return inRange && nextWallInRange;
}

/**
 * A little 2D point position that marks the back corner of the countertop.
 */
class Corner {
    x: number;
    z: number;

    constructor(x: number, z: number)
    {
        this.x = x;
        this.z = z;
    }
}

class Cluster extends Array<Cabinet | Corner | Appliance> {
    sinks: Appliance[] = [];
}

const gatherCabinetClusters = (room: Room) => {
    
    let 
        clusters: Cluster[] = [],
        cluster: Cluster = new Cluster();

    for(const [wall, nextWall] of lookahead(room.walls))
    {
        const boxes: Array<Cabinet | Appliance> = wall.cabinets.filter(needsCountertop);

        const sinks = wall.appliances.filter(a => a.type == 'sink');
        sinks.sort((a, b) => a.position.x - b.position.x);

        for(const appliance of wall.appliances) {
            if(appliance.type == 'dishwasher') {
                boxes.push(appliance);
            }
        }
        // we put the appliance in out-of-order, so we need to sort it
        boxes.sort((a, b) => a.position.x - b.position.x);

        for(const [box, nextCabinet] of lookahead(boxes))
        {
            cluster.push(box);
            
            const
                width = box instanceof Cabinet
                    ? cabinetWidth(box.sku)
                    : box.width,
                rightEdge = box.position.x + width,
                adjacent = nextCabinet != null && rightEdge == nextCabinet.position.x;
            if(adjacent) 
            {
                const i = sinks.findIndex(sink => sink.position.x < rightEdge);
                cluster.sinks.push(...sinks.splice(0, i+1));
                continue;
            } 

            // If we're on the last cabinet on this wall, check if we extend
            // into the 24" depth of the bank of cabinets on the next wall.
            // Note that if this last cabinet
            if(nextCabinet == null
               && nextWall != null
               && box instanceof Cabinet
               && !(box.sku in landmarks))
            {
                // Note that cornerShouldBeFilled is false if the next wall
                // starts with a cabinet that includes landmarks, i.e. some,
                // but not all corner cabinets.
                if(cornerShouldBeFilled(box, wall, nextWall))
                {
                    // This assumes that the room has no transform
                    cluster.push(
                        new Corner(nextWall.position.x, nextWall.position.z));
                    continue;
                }
            }

            clusters.push(cluster);
            cluster = new Cluster();
        }
    }

    return clusters;
};

// TODO: memoize this
const renderCabinetClusters = (room: Room, clusters: Cluster[]): Mesh[] => {
    const
        meshes: Mesh[] = [],
        v = new Vector3(),
        t = new Transform();

    for(const cluster of clusters) {
        const
            forward: Vector2[] = [],
            reverse: Vector2[] = [];

        let lastWasCorner = false;

        for(const [cabinet, nextCabinet] of lookahead(cluster))
        {
            if(cabinet instanceof Corner) {
                reverse.push(new Vector2(cabinet.x, cabinet.z))
                lastWasCorner = true;
                continue;
            }

            if(cabinet instanceof Cabinet) {
                addCountertopPoints(
                    forward, reverse,
                    cabinet, nextCabinet as Cabinet, lastWasCorner,
                    t, v);
            } else if(cabinet instanceof Appliance) {
                const appliance = cabinet;
                t.init(appliance);

                v.set(0, 0, 25)
                t.toWorldPosition(v, v);
                forward.push(new Vector2(v.x, v.z));

                v.set(appliance.width, 0, 25)
                t.toWorldPosition(v, v);
                forward.push(new Vector2(v.x, v.z));

                v.set(0, 0, 0);
                t.toWorldPosition(v, v);
                reverse.push(new Vector2(v.x, v.z));

                v.set(appliance.width, 0, 0);
                t.toWorldPosition(v, v);
                reverse.push(new Vector2(v.x, v.z));
            }

            lastWasCorner = false;
        }

        reverse.reverse();
        const 
            shape = new Shape(),
            first = forward.shift();

        shape.moveTo(first.x, first.y);

//        const app = (window as any).app as Application;
//        let i = 1;
//        const helper = new ThreeAxisHelper(++i);
//        helper.position.set(first.x, 37.5, first.y);
//        app.pipeline.scene.add(helper);

        for(const point of forward.concat(reverse)) {
            shape.lineTo(point.x, point.y); 

//            const helper = new ThreeAxisHelper(++i);
//            helper.position.set(point.x, 37.5, point.y);
//            app.pipeline.scene.add(helper);
        }

        for(const ap of cluster.sinks) {
            const path = new Path();

            const
                x1 = ap.position.x + 0.25,
                x2 = x1 + ap.width - 0.5,
                y1 = ap.position.z + 0.25,
                y2 = y1 + ap.depth - 0.5;
            
            t.init(ap.parent as Wall);
            t.toWorldPosition({x: x1, z: y1, y: 0}, v);
            path.moveTo(v.x, v.z);
            const ax = v.x, ay = v.z;

            t.toWorldPosition({x: x2, z: y1, y: 0}, v);
            path.lineTo(v.x, v.z);

            t.toWorldPosition({x: x2, z: y2, y: 0}, v);
            path.lineTo(v.x, v.z);

            t.toWorldPosition({x: x1, z: y2, y: 0}, v);
            path.lineTo(v.x, v.z);

            path.lineTo(ax, ay); // close the path??

            shape.holes.push(path);
        }

        const geo = new ExtrudeGeometry([shape], {
            bevelEnabled: true,
            // the bevel thickness is additive, so we need to adjust the
            // 3cm depth by the thickness of the bevel.
            depth: 1.1811 - 0.25,
            bevelThickness: 0.125,
            // the bevel size is additive, so we offset the shape in the
            // negative direction to account for it.
            bevelSize: 0.125,
            bevelOffset: -0.125,
        });

        let color: string = '673.png';
        let tint: number = 0xFFFFFF;

        switch(room.line) {
            case 'CT':
                color = '1192.png';
                tint = 0xCCCCCC;
                break;
            case 'PE':
                color = '1047.png';
                tint = 0x553b27;
                break;
            case 'NT':
                color = '673.png';
                tint = 0x8e9ecc;
                break;
            case 'AL':
                color = '1025.png';
                tint = 0xDDDDDD;
                break;
            case 'SG':
                color = '1047.png';
                tint = 0xCCCCCC;
                break;
            case 'SO':
                color = '670.png';
                tint = 0xCCCCCC;
                break;
            case 'ES':
                color = '670.png';
                tint = 0xCCCCCC;
                break;
            case 'DS':
                color = '1155.png';
                tint = 0xF8FFFF;
                break;
            case 'SM':
                color = '670.png';
                tint = 0xCCCCCC;
                break;
            case 'AB':
                color = '670.png';
                tint = 0xCCCCCC;
                break;
            default:
                color = '673.png';
        }

        const mesh = new Mesh(
            geo,
            mfac.make('GlossMelamine', `${config.colors}/${color}`, tint)
        );
        mesh.castShadow = mesh.receiveShadow = true;

        meshes.push(mesh);

        mesh.rotation.set(Math.PI/2, 0, 0);
        mesh.position.set(0, 34.5 + 1.1811 - 0.125, 0);

    }

    return meshes;
}

/**
 * A sub-routine of the render
 */
const addCountertopPoints =
    (forwardOut: Array<Vector2>, reverseOut: Array<Vector2>,
     cabinet: Cabinet, nextCabinet: Cabinet, lastWasCorner: boolean,
     t: Transform, v: Vector3) =>
{
    if(cabinet.sku in landmarks) {
        t.init(cabinet);
        const {forward, reverse} = landmarks[cabinet.sku];
        for(let i=0; i < forward.length; i+=2) {
            v.x = forward[i];
            v.y = 0;
            v.z = forward[i+1];
            t.toWorldPosition(v, v);
            forwardOut.push(new Vector2(v.x, v.z));
        }
        for(let i=0; i < reverse.length; i+=2) {
            v.x = reverse[i];
            v.y = 0;
            v.z = reverse[i+1];
            t.toWorldPosition(v, v);
            reverseOut.push(new Vector2(v.x, v.z));
        }
    } else {
        const
            width = cabinetWidth(cabinet.sku),
            rightEdge = cabinet.position.x + width,
            wall = cabinet.parent as Wall,
            wallLength = wall.heights[wall.heights.length - 1].x;


        // the left edge of the cabinet, adjusted to be 24" if we're just
        // coming out of a corner
        let x1: number = cabinet.position.x;
        if(lastWasCorner) {
            x1 = 24;
        }

        let x2: number = rightEdge;
        if(nextCabinet instanceof Corner) {
            x2 = Math.min(rightEdge, wallLength - 24);
        }

        // we pick up the rotation of the wall, but track the position of
        // the cabinet manually to accomodate adjustments around the
        // corners.
        t.init(cabinet.parent as Wall);

        t.toWorldPosition({x: x1, y: 0, z: 25}, v);
        forwardOut.push(new Vector2(v.x, v.z));

        t.toWorldPosition({x: x2, y: 0, z: 25}, v);
        forwardOut.push(new Vector2(v.x, v.z));

        t.toWorldPosition({x: x1, y: 0, z: 0}, v);
        reverseOut.push(new Vector2(v.x, v.z));

        t.toWorldPosition({x: x2, y: 0, z: 0}, v);
        reverseOut.push(new Vector2(v.x, v.z));
    }
}


export default class CountertopView extends BaseView {
    declare model: Room;

    constructor(model: Room, app: Application) {
        super(model, app);
        this.init();
    }

    protected make(): void {

        const clusters = gatherCabinetClusters(this.model);
        const meshes = renderCabinetClusters(this.model, clusters);

        const group = new Group();

        if(meshes.length > 0) {
            const ref = meshes[0];
            const uvDims = uvDimensions(ref.geometry);
            const scale = 1 / Math.max(uvDims.width, uvDims.height);
            const scaleVec = new Vector2(scale, scale);
            for(const mesh of meshes) {
                uvScale(mesh.geometry, scaleVec);
                group.add(mesh);
            }
        }

        this.add(group);
    }

    protected bind(): void {

    }

}
